import { Link } from 'react-router';
import LegalLayout from './landing/LegalLayout';
import LegalIdentity from './landing/LegalIdentity';
import { usePlatformLegalInfo } from '../hooks/usePlatformLegalInfo';
import { CONTACT } from '../content/site';

function PrivacyPolicy() {
  const info = usePlatformLegalInfo();
  const contactEmail = info.contactEmail || CONTACT.email;

  return (
    <LegalLayout
      title="Privacy policy"
      description="What personal data the EFSoft website collects, why, how long we keep it and how to use your data protection rights."
      path="/privacy-policy"
      lastUpdated="16 October 2026"
    >
      <section>
        <h2>Who we are</h2>
        <p>
          This policy covers the EFSoft website and the chat on it. EFSoft is
          the data controller for the personal data described here.
        </p>
        <LegalIdentity info={info} />
      </section>

      <section>
        <h2>What we collect</h2>
        <ul>
          <li>
            When you email or call us: your name, your contact details and
            whatever you tell us about your project.
          </li>
          <li>
            When you use the chat: the messages you type. They are sent to our
            chat service, which uses an AI model to write the replies.
          </li>
          <li>
            Technical data that our hosting provider records when your browser
            requests a page, such as your IP address, the browser you use and
            the time of the request.
          </li>
        </ul>
        <p>
          We do not use analytics or advertising cookies, and we do not sell
          personal data.
        </p>
      </section>

      <section>
        <h2>Why we use it and on what legal basis</h2>
        <ul>
          <li>
            To answer your enquiry and, if you ask us to, prepare an estimate
            or agreement. The legal basis is taking steps at your request
            before entering into a contract (GDPR Article 6(1)(b)).
          </li>
          <li>
            To keep the website and chat working and secure. The legal basis
            is our legitimate interest in running a safe website (GDPR Article
            6(1)(f)).
          </li>
        </ul>
      </section>

      <section>
        <h2>Cookies</h2>
        <p>
          The site sets one cookie, and only after you open the chat. It is
          called <code>senderId</code>, holds a random identifier so the chat
          can keep your conversation together, and expires after 365 days. The
          chat cannot work without it, so it is not used for anything else. You
          can delete it at any time in your browser settings.
        </p>
      </section>

      <section>
        <h2>How long we keep it</h2>
        <p>
          We keep enquiry emails and chat conversations for as long as we need
          them to deal with your enquiry and any project that follows, and for
          any longer period the law requires, for example for accounting
          records.
          {info.logRetentionDays !== null &&
            ` Server logs are deleted after ${info.logRetentionDays} days.`}
        </p>
      </section>

      <section>
        <h2>Who else processes it</h2>
        <p>
          The website and the chat service run on Microsoft Azure
          {info.hostingRegion ? ` (${info.hostingRegion})` : ''}, which stores
          and processes the data on our behalf. We share personal data with
          nobody else unless the law requires us to.
        </p>
      </section>

      <section>
        <h2>Your rights</h2>
        <p>Under data protection law you can ask us to:</p>
        <ul>
          <li>give you a copy of the personal data we hold about you</li>
          <li>correct data that is wrong</li>
          <li>
            delete your data (see{' '}
            <Link to="/data-deletion">how to request deletion</Link>)
          </li>
          <li>limit how we use it, or stop using it, where the law allows</li>
          <li>send your data to you or another organisation in a usable format</li>
        </ul>
        <p>
          You can also complain to{' '}
          {info.supervisoryAuthority
            ? info.supervisoryAuthority
            : 'the data protection authority in the country where you live or work'}
          . We would appreciate the chance to sort out your concern first.
        </p>
      </section>

      <section>
        <h2>Changes to this policy</h2>
        <p>
          When we change this policy we update the date at the top of the page.
        </p>
      </section>

      <div className="legal-callout">
        <p>
          Questions about your data? Email us at{' '}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
        </p>
      </div>
    </LegalLayout>
  );
}

export default PrivacyPolicy;
